import { useAtom } from 'jotai';
import { startCase, uniqBy } from 'lodash';
import { useMemo } from 'react';

import type { SearchableSelectConfig } from '@/components/SearchableSelect';
import SearchBar, {
  addZeroCountOptions,
  getSearchableSelectOptions
} from '@/features/SearchBar';
import {
  ALERT_RULES_SEARCHABLE_SELECT_FILTERS,
  ALERT_RULES_TUNING_OPTIONS
} from '@/pages/AlertRules/AlertRules.constants';
import {
  alertRulesFiltersAtom,
  filteredAlertRulesAtom
} from '@/pages/AlertRules/AlertRules.store';
import { selectedOrganizationIdAtom } from '@/pages/Organizations/Organizations.store';

const AlertRulesSearch = () => {
  const [alertRulesFilters, setAlertRulesFilters] = useAtom(
    alertRulesFiltersAtom
  );
  const [filteredAlertRules] = useAtom(filteredAlertRulesAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);

  const alertRulesSearchableFilters = useMemo(() => {
    const searchableSelectFilters = ALERT_RULES_SEARCHABLE_SELECT_FILTERS.map(
      (filter) => {
        let filterOptions = getSearchableSelectOptions(
          filteredAlertRules,
          filter.field,
          filter.title,
          filter.filterOptions
        );

        if (alertRulesFilters?.[filter.field]) {
          // Required to handle cases where the label needs to be formatted differently than just the value (e.g. enabled/disabled labels)
          const filterLabelFormatter = (value: string) => {
            const matchedOption = filter.filterOptions.find(
              (option) => option.value === value
            );
            return matchedOption ? String(matchedOption.label) : value;
          };
          const updatedFilters = addZeroCountOptions(
            filter as SearchableSelectConfig,
            alertRulesFilters,
            filterOptions,
            filterLabelFormatter
          );
          filterOptions = updatedFilters;
        }
        if (filter.field === 'tuning') {
          const orgInstructionsOptions = filteredAlertRules.filter((rule) => {
            if (
              !rule.organization_instructions ||
              rule.organization_instructions.length === 0
            ) {
              return false;
            }

            if (selectedOrganizationId === 0) {
              return rule.organization_instructions.some(
                (instruction) => instruction?.instructions !== null
              );
            }

            return rule.organization_instructions.some(
              (instruction) =>
                instruction?.organization_id === selectedOrganizationId &&
                instruction?.instructions !== null
            );
          });
          const orgInstructionsCount = orgInstructionsOptions.length;

          const globalInstructionsCount = filteredAlertRules.filter(
            (rule) => rule.instructions
          ).length;

          filterOptions = ALERT_RULES_TUNING_OPTIONS.map((key) => ({
            label: startCase(key),
            value: key,
            count:
              key === 'organization'
                ? orgInstructionsCount
                : globalInstructionsCount,
            id: key
          }));
        }

        // merge the default filter options with the calculated options
        filterOptions = uniqBy(
          [...filterOptions, ...filter.filterOptions],
          'value'
        );

        // if options are defined in the constants, ensure the sort order is maintained
        if (filter.filterOptions.length > 0) {
          const sortingValues = filter.filterOptions.map(
            (option) => option.value
          );

          filterOptions = filterOptions.sort((a, b) =>
            sortingValues.indexOf(a.value as string) >
            sortingValues.indexOf(b.value as string)
              ? 1
              : -1
          );
        }
        return {
          ...filter,
          filterOptions
        } as SearchableSelectConfig;
      }
    );

    return searchableSelectFilters;
  }, [filteredAlertRules, selectedOrganizationId]);

  return (
    <SearchBar
      className="px-8 pb-4"
      searchableSelectFilters={alertRulesSearchableFilters}
      selectedFilters={alertRulesFilters}
      onFilterChange={setAlertRulesFilters}
    />
  );
};

export default AlertRulesSearch;
